import React from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Dimensions } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface LoadingCampsProps {
    isLocating?: boolean;
    radius?: number;
    mode?: 'camps' | 'sessions';
}

const WIDTH = Dimensions.get('window').width * 0.166; // Align roughly with card width

const LoadingCamps: React.FC<LoadingCampsProps> = ({ isLocating = false, radius = 20, mode = 'camps' }) => {
    const skeletons = Array.from({ length: 6 });

    const title = mode === 'sessions'
        ? 'Loading sessions…'
        : (isLocating ? 'Getting your location…' : `Searching camps within ${radius} miles…`);

    return (
        <View style={styles.container}>
            <View style={styles.headerRow}>
                <MaterialCommunityIcons name={mode === 'sessions' ? 'calendar-clock' : (isLocating ? 'crosshairs-gps' : 'map-search')} size={22} color={'#3C8248'} />
                <Text style={styles.titleText}>{title}</Text>
            </View>

            <View style={styles.subTextRow}>
                <ActivityIndicator size="small" color="#3C8248" />
                <Text style={styles.subText}>This may take a few seconds.</Text>
            </View>

            {isLocating && mode !== 'sessions' && (
                <Text style={styles.tipText}>Tip: Allow location access for more accurate results.</Text>
            )}

            <View style={styles.skeletonGrid}>
                {skeletons.map((_, idx) => (
                    <View key={idx} style={styles.cardSkeleton}>
                        <View style={styles.imageSkeleton} />
                        <View style={styles.lineSkeletonWide} />
                        <View style={styles.lineSkeleton} />
                        <View style={styles.badgeRow}>
                            <View style={styles.badgeSkeleton} />
                            <View style={[styles.badgeSkeleton, { width: 70 }]} />
                        </View>
                    </View>
                ))}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 30,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 6,
    },
    titleText: {
        fontSize: 16,
        fontWeight: '700',
        color: '#1a1a1a',
    },
    subTextRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 6,
    },
    subText: {
        fontSize: 13,
        color: '#555',
    },
    tipText: {
        fontSize: 12,
        color: '#777',
        marginBottom: 16,
    },
    skeletonGrid: {
        width: '100%',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 16,
        paddingHorizontal: 20,
    },
    cardSkeleton: {
        width: WIDTH,
        minWidth: 240,
        backgroundColor: '#fff',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#eef0f3',
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 6,
    },
    imageSkeleton: {
        height: 120,
        backgroundColor: '#f0f2f5',
    },
    lineSkeletonWide: {
        height: 14,
        backgroundColor: '#f3f5f8',
        marginTop: 12,
        marginHorizontal: 12,
        borderRadius: 6,
    },
    lineSkeleton: {
        height: 12,
        backgroundColor: '#f3f5f8',
        marginTop: 8,
        marginBottom: 12,
        marginHorizontal: 12,
        borderRadius: 6,
        width: '70%'
    },
    badgeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 12,
        paddingBottom: 12,
    },
    badgeSkeleton: {
        height: 18,
        width: 90,
        backgroundColor: '#eef2f7',
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#e6ebf1',
    }
});

export default LoadingCamps;


